import React from "react";
import { Link as RouterLink } from "react-router-dom";
import classNames from "classnames";

import { Game } from "../../models/Game";
import { PromoTile } from "../../models/PromoTile";
import { GridCell } from "../../atoms/Layout/GridCell/GridCell";
import { GridRow } from "../../atoms/Layout/GridRow/GridRow";
import { Text } from "../../atoms/Text/Text";
import { Ribbon } from "../../atoms/Ribbon/Ribbon";
import styles from "./Tiles.css";
import { RewardLabel } from "../../atoms/RewardLabel/RewardLabel";
import { Membership } from "../../atoms/Icons/MembershipIcon";
import { isMobileDevice } from "../../../utils";

type CarouselTileProps = {
  promoTile: PromoTile;
  elementDescription?: string;
  className?: string;
  onClick?: any;
  style?: { [key: string]: string };
};

export const CarouselTile = React.memo(
  ({
    promoTile,
    className,
    elementDescription,
    style,
    onClick,
  }: CarouselTileProps) => {
    let ribbon = null;
    let rewardLabel = null;
    let memberSign = null;
    let attributes = null;
    const gameCurrent = promoTile.game as Game;
    if (gameCurrent) {
      const game = new Game(gameCurrent);
      ribbon = <Ribbon size="large" game={game} />;
      rewardLabel = <RewardLabel size="large" game={game} />;
      memberSign = game.membersOnly ? (
        <MemberSign size="large" isStayingSharp={game.isStayingSharp()} />
      ) : null;
      attributes = game.isSpain() ? { lang: "es" } : null;
    }

    return (
      <PromoTileContainer
        size={12}
        className={className}
        data-element-description={elementDescription}
        style={style}
        {...attributes}
      >
        <GridRow className={styles.promoFullHeightRow}>
          <ContentCell className={styles.relative} size={[8, 12]}>
            <ImageLink
              game={promoTile.game}
              data-element-description="promo tile image link"
              to={promoTile.link}
              href={promoTile.link}
              target={promoTile.target}
              onClick={onClick}
            >
              <ImgContainer>
                {ribbon}
                {rewardLabel}
                <Img
                  src={promoTile.imageSrc}
                  alt={promoTile.alt}
                  title={promoTile.alt}
                />
              </ImgContainer>
            </ImageLink>
            {memberSign}
          </ContentCell>
          <ContentCell size={[4, 12]}>
            <PromoDescription>
              <PromoName
                size="h2"
                bold={true}
                data-element-description="promo tile name"
              >
                {promoTile.title}
              </PromoName>

              <PromoText
                size="md"
                align="center"
                isDangerously={true}
                data-element-description="promo tile description"
                content={promoTile.description}
              />

              <PlayLink
                game={promoTile.game}
                aria-label={`${promoTile.buttonTitle}, ${promoTile.title}`}
                data-element-description="promo tile play link"
                to={promoTile.link}
                href={promoTile.link}
                onClick={onClick}
                target={promoTile.target}
                isHidden={promoTile.link == null}
              >
                <Text bold={true}>{promoTile.buttonTitle}</Text>
              </PlayLink>
            </PromoDescription>
          </ContentCell>
        </GridRow>
      </PromoTileContainer>
    );
  }
);

const PromoTileContainer = (props: any) => (
  <GridCell className={styles.promoTileContainer} {...props} />
);
const ContentCell = (props: any) => (
  <GridCell className={styles.promoContentCell} {...props} />
);
const ImgContainer = (props: any) => (
  <div className={styles.promoImgContainer} {...props} />
);
const Img = (props: any) => <img className={styles.promoImg} {...props} />;
const ImageLink = (props: any) => {
  if (props.to == null) {
    return <span className={styles.promoLink} {...props} />;
  }

  const gameCurrent = props.game as Game;
  return gameCurrent && !gameCurrent.seo_IsFakeGame ? (
    <RouterLink className={styles.promoLink} {...props} />
  ) : (
    <a className={styles.promoLink} {...props} />
  );
};

const PromoDescription = (props: any) => (
  <div
    className={
      !isMobileDevice()
        ? styles.promoDescription
        : styles.mobilePromoDescription
    }
    {...props}
  />
);
const PromoName = (props: any) => (
  <Text className={styles.promoName} {...props} />
);
const PromoText = (props: any) => (
  <Text className={styles.promoText} {...props} />
);
const PlayLink = ({ isHidden, ...props }: any) => {
  const gameCurrent = props.game as Game;

  return gameCurrent && !gameCurrent.seo_IsFakeGame ? (
    <RouterLink
      className={classNames(styles.promoPlayLink, {
        [styles.isHide]: !!isHidden,
      })}
      {...props}
    />
  ) : (
    <a
      className={classNames(styles.promoPlayLink, {
        [styles.isHide]: !!isHidden,
      })}
      {...props}
    />
  );
};

const MemberSign = (props: any) => (
  <Membership className={styles.memberSign} {...props} />
);
